/* PPVision: faces and people in a customer's photo, found in their own browser, for free (Daniel and Yonatan's
   19 Oct sync: "auto-centre uploaded photos on faces so faces aren't cropped"; "try a Remove background button").

   Two questions, each answered or null, never thrown:
     PPVision.faces(src)   -> [{ x, y, w, h, score }] in fractions of the picture (0..1), [] for "looked, none", or
                              null when it could not look (no network, no WebAssembly, an old engine).
     PPVision.person(src)  -> a PNG Blob the picture's size: white where the person is, transparent elsewhere (a mask
                              to cut the picture with), or null (could not look, or nobody there).

   ⛔ NOTHING HERE MAY SLOW AN UPLOAD. Nothing loads until the first question, which configurator.js asks after the
   photos are on the card, and every caller treats null as "leave the picture as it is".

   The models are Google's MediaPipe (Apache 2.0): the library from jsDelivr at a pinned version, the two models
   (BlazeFace short range, 230 KB; the selfie segmenter, 250 KB) copied byte for byte into the theme's assets
   (pp-face-short.tflite, pp-person-seg.tflite; product-builder.liquid hands their URLs over as PP_VISION), with
   Google's bucket as the fallback.
   ⛔ WHY OUR OWN COPY (19 Oct): on our product page, Firefox fetched the bucket once, then stalled every later
   request to storage.googleapis.com for 30 s and failed it, so the second model never loaded (the face pan and the
   cut-out both answered null). On a blank page the same two downloads worked; jsDelivr and our CDN were unaffected.
   Same origin as the page, our copy needs no CORS. Each download is ours, with a timeout, and MediaPipe is
   handed the bytes. CPU, not GPU: the GPU delegate
   needs WebGL2 in a worker-free page and fails differently in each engine, and these are one-off stills.
   The browser's own FaceDetector (Shape Detection API) is asked first where it exists; it costs no download. */
(function () {
  'use strict';
  const TASKS = 'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.21';
  const OWN = window.PP_VISION || {};
  const FACE_MODEL = [OWN.face, 'https://storage.googleapis.com/mediapipe-models/face_detector/blaze_face_short_range/float16/1/blaze_face_short_range.tflite'];
  const PERSON_MODEL = [OWN.person, 'https://storage.googleapis.com/mediapipe-models/image_segmenter/selfie_segmenter/float16/1/selfie_segmenter.tflite'];
  const MODEL_TIMEOUT_MS = 15000;
  const MIN_SCORE = 0.6;          // a face below this is more often a pattern than a person
  const MIN_PERSON = 0.03;        // under 3 % of the picture is "nobody there", not a person to cut out

  let lib = null, fileset = null, faceTask = null, personTask = null;
  let queue = Promise.resolve();
  // One model call at a time: four photos at once would load the model four times and fight for the main thread.
  function serial(fn) {
    const run = queue.then(fn, fn);
    queue = run.catch(function () {});
    return run;
  }

  function vision() {
    if (!lib) {
      lib = import(TASKS + '/vision_bundle.mjs').then(function (v) {
        fileset = v.FilesetResolver.forVisionTasks(TASKS + '/wasm');
        return v;
      });
      lib.catch(function () { lib = null; });   // a failed load may be retried on the next photo
    }
    return lib;
  }

  // The model's bytes from the first URL that answers in time.
  async function modelBytes(urls) {
    for (const u of urls) {
      if (!u) continue;
      const ac = typeof AbortController === 'function' ? new AbortController() : null;
      const to = ac ? setTimeout(function () { ac.abort(); }, MODEL_TIMEOUT_MS) : 0;
      try {
        const res = await fetch(u, ac ? { signal: ac.signal } : {});
        if (res.ok) return new Uint8Array(await res.arrayBuffer());
      } catch (e) { /* the next one */ } finally { clearTimeout(to); }
    }
    throw new Error('model');
  }

  function image(src) {
    return new Promise(function (resolve, reject) {
      const im = new Image();
      im.onload = function () { resolve(im); };
      im.onerror = function () { reject(new Error('image')); };
      im.src = src;
    });
  }

  async function faceDetector() {
    if (faceTask) return faceTask;
    const v = await vision();
    faceTask = await v.FaceDetector.createFromOptions(await fileset, {
      baseOptions: { modelAssetBuffer: await modelBytes(FACE_MODEL), delegate: 'CPU' },
      runningMode: 'IMAGE',
      minDetectionConfidence: MIN_SCORE,
    });
    return faceTask;
  }

  /* BlazeFace short range sees the whole picture at 128 px, so faces in a wide group shot can be too small for it.
     When the whole picture finds none, three squares along its long side are asked as well, each at its own 128. */
  function squares(w, h) {
    const s = Math.min(w, h), long = Math.max(w, h);
    if (long / s < 1.25) return [];
    const out = [];
    for (let k = 0; k < 3; k++) {
      const o = Math.round((long - s) * k / 2);
      out.push(w >= h ? { x: o, y: 0, s: s } : { x: 0, y: o, s: s });
    }
    return out;
  }

  async function mediapipeFaces(im) {
    const det = await faceDetector();
    const W = im.naturalWidth, H = im.naturalHeight;
    const read = function (res, ox, oy) {
      return (res.detections || []).map(function (d) {
        const b = d.boundingBox;
        return { x: (b.originX + ox) / W, y: (b.originY + oy) / H, w: b.width / W, h: b.height / H,
                 score: (d.categories && d.categories[0] && d.categories[0].score) || 0 };
      });
    };
    let found = read(det.detect(im), 0, 0);
    if (!found.length) {
      const c = document.createElement('canvas');
      for (const q of squares(W, H)) {
        c.width = c.height = q.s;
        c.getContext('2d').drawImage(im, q.x, q.y, q.s, q.s, 0, 0, q.s, q.s);
        found = found.concat(read(det.detect(c), q.x, q.y));
      }
    }
    return found;
  }

  async function nativeFaces(im) {
    if (typeof window.FaceDetector !== 'function') return null;
    try {
      const fd = new window.FaceDetector({ fastMode: false, maxDetectedFaces: 10 });
      const W = im.naturalWidth, H = im.naturalHeight;
      const got = await fd.detect(im);
      return got.map(function (f) {
        const b = f.boundingBox;
        return { x: b.x / W, y: b.y / H, w: b.width / W, h: b.height / H, score: 1 };
      });
    } catch (e) { return null; }
  }

  function faces(src) {
    return serial(async function () {
      try {
        const im = await image(src);
        const n = await nativeFaces(im);
        if (n && n.length) return n;
        const found = await mediapipeFaces(im);
        return found.filter(function (f) { return f.score >= MIN_SCORE && f.w > 0 && f.h > 0; });
      } catch (e) { return null; }
    });
  }

  async function segmenter() {
    if (personTask) return personTask;
    const v = await vision();
    personTask = await v.ImageSegmenter.createFromOptions(await fileset, {
      baseOptions: { modelAssetBuffer: await modelBytes(PERSON_MODEL), delegate: 'CPU' },
      runningMode: 'IMAGE',
      outputConfidenceMasks: true,
      outputCategoryMask: false,
    });
    return personTask;
  }

  /* The selfie segmenter answers at 256 x 256. Its confidence becomes the mask's alpha through a soft step (0.35 to
     0.65), so the edge is a short ramp rather than a halo of half-sure pixels, and is then drawn up to the picture's
     size with smoothing, which is all the anti-aliasing a 256 px mask can have. */
  function person(src) {
    return serial(async function () {
      let res = null;
      try {
        const im = await image(src);
        const seg = await segmenter();
        res = seg.segment(im);
        const masks = res.confidenceMasks || [];
        const m = masks.length > 1 ? masks[1] : masks[0];
        if (!m) return null;
        const conf = m.getAsFloat32Array(), mw = m.width, mh = m.height;
        const small = document.createElement('canvas');
        small.width = mw; small.height = mh;
        const sx = small.getContext('2d');
        const px = sx.createImageData(mw, mh);
        let on = 0;
        for (let k = 0; k < conf.length; k++) {
          const t = Math.max(0, Math.min(1, (conf[k] - 0.35) / 0.3));
          const a = t * t * (3 - 2 * t);
          if (a > 0.5) on++;
          px.data[k * 4] = px.data[k * 4 + 1] = px.data[k * 4 + 2] = 255;
          px.data[k * 4 + 3] = Math.round(a * 255);
        }
        if (on / conf.length < MIN_PERSON) return null;
        sx.putImageData(px, 0, 0);
        const big = document.createElement('canvas');
        big.width = im.naturalWidth; big.height = im.naturalHeight;
        const bx = big.getContext('2d');
        bx.imageSmoothingEnabled = true;
        bx.imageSmoothingQuality = 'high';
        bx.drawImage(small, 0, 0, big.width, big.height);
        return await new Promise(function (r) { big.toBlob(r, 'image/png'); });
      } catch (e) {
        return null;
      } finally {
        try { if (res && res.close) res.close(); } catch (e) { /* nothing to free */ }
      }
    });
  }

  window.PPVision = { faces: faces, person: person };
})();
